import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {isSnapshotPeriodIncomplete, snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import {nullableMoneyMajorUnitAmount} from "../../utils/reportingMetricRatios.js";
import {calculateCompleteCampaignProfit, CampaignSegment, campaignPurchaseCostQuality, classifyCampaignSegment, combineMediaSourceQualities, mediaFinanceScopeLabel, mediaSourceQualityBlocksMetrics, rankCompleteAboveBreakEvenCampaigns} from "../../utils/reportingMetricPresentation.js";
import {partialProfitabilityChartPoint, selectProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import {
    ChannelMixColors,
    compactChannelName,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    ExpandablePanelTitle,
    filterRowsBySearch,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
    SortableHeader,
    TablePagination,
    TableSearch,
    useSortableRows,
} from "./ReportingLab.jsx";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";

function getCampaignMetric(campaign, metricBasis, key) {
    const suffix = metricBasis === "completed" ? "Net" : "Gross";
    return campaign?.[`${key}${suffix}`];
}

export function campaignMetricTooltip(metric, t) {
    const copy = {
        ctr: t("reporting_lab.campaigns.ctr_help", {defaultValue: "CTR = kliknutí vykázaná reklamním systémem kampaně / jeho zobrazení × 100 %."}),
        cpc: t("reporting_lab.campaigns.cpc_help", {defaultValue: "CPC vypočtené Datixem = náklady kampaně / kliknutí vykázaná jejím reklamním systémem. CPC uvedené přímo v reklamním systému se může lišit."}),
        cpm: t("reporting_lab.campaigns.cpm_help", {defaultValue: "CPM vypočtené Datixem = náklady kampaně / zobrazení vykázaná jejím reklamním systémem × 1 000."}),
        conversionRate: t("reporting_lab.campaigns.conversion_rate_help", {defaultValue: "Nestornované objednávky přiřazené kampani (včetně objednávek bez známého stavu) / kliknutí vykázaná jejím reklamním systémem × 100 %. Jde o výpočet Datixa, nikoli o konverze vykázané platformou."}),
    };
    return copy[metric];
}

export function buildCampaignRows(channelData, t, metricBasis = "completed") {
    const rows = [];

    channelData.forEach(({channel, overview}) => {
        const channelName = compactChannelName(channel);
        const campaigns = overview?.adAccounts?.flatMap((account) => account.campaigns ?? []) ?? [];

        campaigns.forEach((campaign) => {
            const spend = campaign.spendMarketing;
            const mediaQualityBlocked = campaign.financeScope === "raw_provider" || mediaSourceQualityBlocksMetrics(campaign.mediaSourceQuality);
            const turnover = getCampaignMetric(campaign, metricBasis, "turnover");
            const profit = getCampaignMetric(campaign, metricBasis, "profit");
            // OpenAPI deserialization keeps omitted optional properties as keys with an
            // undefined value. The value itself is therefore the permission boundary:
            // when the backend redacts profit metrics, neither profit nor POAS may be
            // rendered or used for profitability classification.
            const suffix = metricBasis === "completed" ? "Net" : "Gross";
            const hasCompleteCosts = metricBasis !== "completed" || campaign[`purchaseCostComplete${suffix}`] === true;
            const fullProfitAvailable = !mediaQualityBlocked && profit != null && hasCompleteCosts;
            const fullPoasAvailable = !mediaQualityBlocked && campaign[`poasBeforeMarketing${suffix}`] != null && hasCompleteCosts;
            const profitMetric = mediaQualityBlocked ? {value: null, partial: false} : selectProfitabilityMetric({
                fullValue: profit,
                fullComplete: fullProfitAvailable,
                partialProfitability: metricBasis === "completed" ? campaign.partialProfitability : campaign.partialProfitabilityMarketing,
                partialField: "knownProfitAfterAllMarketing",
            });
            const poasMetric = mediaQualityBlocked ? {value: null, partial: false} : selectProfitabilityMetric({
                fullValue: campaign[`poasBeforeMarketing${suffix}`],
                fullComplete: fullPoasAvailable,
                partialProfitability: metricBasis === "completed" ? campaign.partialProfitability : campaign.partialProfitabilityMarketing,
                partialField: "knownPoas",
            });
            const hasProfit = fullProfitAvailable;
            const hasPoas = fullPoasAvailable;
            const orders = Number(getCampaignMetric(campaign, metricBasis, "orders") ?? 0);
            const poas = poasMetric.value ?? null;
            const roas = mediaQualityBlocked ? null : campaign[`roas${suffix}`] ?? null;
            const pno = mediaQualityBlocked ? null : campaign[`pno${suffix}`] ?? null;
            const spendAmount = moneyAmount(spend);
            const turnoverAmount = moneyAmount(turnover);
            const profitAmount = moneyAmount(profit);
            const clicks = Number(campaign.clicks ?? 0);
            const impressions = Number(campaign.impressions ?? 0);

            let segment = classifyCampaignSegment({
                spend: spendAmount,
                turnover: turnoverAmount,
                profit: profitAmount,
                orders,
                hasProfit,
                hasPoas,
                poas,
            });
            segment = hasProfit && hasPoas && metricBasis !== "completed" ? (campaign.performanceSegmentGross ?? segment) : segment;

            rows.push({
                id: `${channel?.id ?? channelName}-${campaign.remoteId ?? campaign.name}`,
                channel,
                channelName,
                campaign,
                ...campaignPurchaseCostQuality(campaign, metricBasis),
                name: campaign.name ?? t("reporting_lab.campaigns.unknown_campaign", {defaultValue: "Neznámá kampaň"}),
                status: campaign.statusName ?? campaign.status ?? "-",
                spend,
                turnover,
                ...(profitMetric.value != null ? {profit: profitMetric.value} : {}),
                profitIsPartial: profitMetric.partial,
                profitClassificationAvailable: hasProfit && hasPoas,
                partialProfitability: mediaQualityBlocked ? null : metricBasis === "completed" ? campaign.partialProfitability : campaign.partialProfitabilityMarketing,
                mediaSourceQuality: campaign.mediaSourceQuality,
                financeScope: campaign.financeScope,
                orders,
                ...(poasMetric.value != null ? {poas} : {}),
                poasIsPartial: poasMetric.partial,
                roas,
                pno,
                cpa: mediaQualityBlocked ? null : campaign.cpa,
                clicks,
                impressions,
                ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
                cpc: !mediaQualityBlocked && clicks > 0 ? {...spend, amount: spendAmount / clicks} : null,
                cpm: !mediaQualityBlocked && impressions > 0 ? {...spend, amount: spendAmount / impressions * 1000} : null,
                costPerOrder: !mediaQualityBlocked && orders > 0 ? {...spend, amount: spendAmount / orders} : null,
                conversionRate: clicks > 0 ? (orders / clicks) * 100 : null,
                averageOrderValue: turnover != null && orders > 0 ? {...turnover, amount: moneyAmount(turnover) / orders} : null,
                segment,
            });
        });
    });

    return rows
        .filter((row) => moneyAmount(row.spend) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0 || row.clicks > 0)
        .sort((a, b) => moneyAmount(b.spend) - moneyAmount(a.spend));
}

export function campaignOverallProfitMetric(totalProfit, partialProfitability, snapshotIncomplete = false, mediaSourceQuality = null) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) return {value: null, partial: false, allowed: false};
    if (!snapshotIncomplete && partialProfitability != null) {
        return selectProfitabilityMetric({
            fullValue: partialProfitability.knownProfitAfterAllMarketing,
            fullComplete: partialProfitability.complete === true,
            partialProfitability,
            partialField: "knownProfitAfterAllMarketing",
        });
    }

    return selectProfitabilityMetric({
        fullValue: totalProfit === null || totalProfit === undefined ? undefined : totalProfit,
        fullComplete: totalProfit !== null && totalProfit !== undefined,
        partialProfitability: undefined,
        partialField: "knownProfitAfterAllMarketing",
    });
}

export function campaignFullEstimatedCostCount(campaigns, metricBasis = "completed") {
    const suffix = metricBasis === "completed" ? "Net" : "Gross";
    return campaigns.reduce((count, campaign) => {
        if (campaign[`purchaseCostComplete${suffix}`] !== true) return count;
        const estimated = Number(campaign[`purchaseCostEstimated${suffix}`]);
        return Number.isFinite(estimated) && estimated > 0 ? count + estimated : count;
    }, 0);
}

export function campaignAggregateEstimatedCostCount(partialProfitability, campaigns, metricBasis = "completed") {
    const globalCount = partialProfitability?.estimatedLineCount;
    if (globalCount !== null && globalCount !== undefined && globalCount !== ""
        && Number.isFinite(Number(globalCount)) && Number(globalCount) >= 0) {
        return Number(globalCount);
    }
    return campaignFullEstimatedCostCount(campaigns, metricBasis);
}

export function settleCampaignsRequest(request, {isActive, onSuccess, onError, onFinally}) {
    return request
        .then((result) => { if (isActive()) onSuccess(result); })
        .catch((error) => { if (isActive()) onError(error); })
        .finally(() => { if (isActive()) onFinally(); });
}

export function launchCampaignsRequest(requestFactory, {signal, isActive, onSuccess, onError, onFinally}) {
    return Promise.resolve().then(() => {
        if (signal.aborted || !isActive()) return undefined;

        return settleCampaignsRequest(requestFactory(), {
            isActive: () => !signal.aborted && isActive(),
            onSuccess,
            onError,
            onFinally,
        });
    });
}

export function campaignsLoadErrorAlert(errorMessage, t) {
    return {
        id: "campaigns-load-error",
        type: "error",
        showClose: false,
        title: t("google_reporting.load_error_title", {defaultValue: "Část přehledu se nepodařilo načíst"}),
        content: errorMessage || t("google_reporting.load_error", {defaultValue: "Nedostupná data nejsou započítaná jako nula. Zkuste přehled načíst znovu; pokud problém trvá, zkontrolujte stav integrace."}),
    };
}

function campaignTableValue(row, key) {
    return key === "campaign" ? row.name : key === "channel" ? row.channelName : row[key];
}

function segmentMeta(t, profitClassification) {
    return {
        [CampaignSegment.AboveBreakEven]: {
            label: profitClassification
                ? t("reporting_lab.campaigns.segment.above_break_even", {defaultValue: "Ziskové"})
                : t("reporting_lab.campaigns.segment.performing", {defaultValue: "Výkonné"}),
            tone: "good",
            className: "border-emerald-200 bg-emerald-50 text-emerald-700",
        },
        [CampaignSegment.Loss]: {
            label: profitClassification
                ? t("reporting_lab.campaigns.segment.loss", {defaultValue: "Ztrátové"})
                : t("reporting_lab.campaigns.segment.underperforming", {defaultValue: "Nevýkonné"}),
            tone: "bad",
            className: "border-rose-200 bg-rose-50 text-datixo-negative",
        },
        [CampaignSegment.LowData]: {
            label: t("reporting_lab.campaigns.segment.low_data", {defaultValue: "Málo dat"}),
            tone: "default",
            className: "border-slate-200 bg-white text-slate-500",
        },
    };
}

function SegmentBadge({segment, meta}) {
    return <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${meta[segment]?.className ?? meta[CampaignSegment.LowData].className}`}>
        {meta[segment]?.label ?? segment}
    </span>;
}

export function campaignDescriptionKey(snapshot, usesProfitClassification) {
    if (!snapshotHasPublishedDays(snapshot)) {
        return null;
    }

    return usesProfitClassification
        ? "reporting_lab.descriptions.campaigns.text"
        : "reporting_lab.descriptions.campaigns.marketing_text";
}

export default function ReportingCampaignsLab({project}) {
    const {t, i18n} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderRatioValue = (value) => formatChartMultiplier(value);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [overallPartialProfitability, setOverallPartialProfitability] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [loadError, setLoadError] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState("");
    const [selectedSegment, setSelectedSegment] = useState("all");
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const tableExpandedStorageKey = `reporting-lab:campaign-table-expanded:${project?.id ?? "unknown"}`;
    const [tableExpanded, setTableExpanded] = useState(() => typeof window !== "undefined" && window.sessionStorage.getItem(tableExpandedStorageKey) === "1");

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        window.sessionStorage.setItem(tableExpandedStorageKey, tableExpanded ? "1" : "0");
    }, [tableExpanded, tableExpandedStorageKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        const controller = new AbortController();
        setLoading(true);
        setLoadError(false);
        setLoadErrorMessage("");
        setChannelData([]);
        setSnapshot(null);
        setOverallPartialProfitability(undefined);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        launchCampaignsRequest(() => projectApi.marketingChannelOverviews(
            {projectId: project.id, erpFilter},
            {signal: controller.signal},
        ), {
            signal: controller.signal,
            isActive: () => active,
            onSuccess: (result) => {
                setChannelData((result.items ?? []).map((item) => ({
                    channel: {id: item.id, name: item.name},
                    overview: item.overview,
                })));
                setSnapshot(result.snapshot);
                setOverallPartialProfitability(result.partialProfitabilityMarketing);
            },
            onError: (error) => {
                setLoadError(true);
                et(error).then((message) => { if (active && message) setLoadErrorMessage(message); }).catch(() => {});
            },
            onFinally: () => setLoading(false),
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [project?.id, erpFilter]);

    const metricBasis = "marketing";
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const campaigns = useMemo(() => {
        const rows = snapshotRowsForPublishedDays(buildCampaignRows(channelData, t, metricBasis), snapshot);
        if (!isSnapshotPeriodIncomplete(snapshot) || !summaryUnavailable) return rows;
        return rows.map((row) => ({
            ...row,
            ...(row.profitIsPartial ? {profit: undefined} : {}),
            ...(row.poasIsPartial ? {poas: undefined} : {}),
            profitIsPartial: false,
            poasIsPartial: false,
            partialProfitability: undefined,
        }));
    }, [channelData, t, metricBasis, snapshot, summaryUnavailable]);
    const canonicalCampaigns = campaigns.filter((campaign) => campaign.financeScope !== "raw_provider");
    const campaignMediaQuality = snapshot?.mediaSourceQuality ?? combineMediaSourceQualities(campaigns.map((campaign) => campaign.mediaSourceQuality));
    const totalProfit = summaryUnavailable ? null : calculateCompleteCampaignProfit(canonicalCampaigns, metricBasis);
    const marketingPartialProfitability = overallPartialProfitability;
    const totalProfitMetric = campaignOverallProfitMetric(totalProfit, marketingPartialProfitability, summaryUnavailable, campaignMediaQuality);
    const completeEstimatedCostCount = campaignAggregateEstimatedCostCount(marketingPartialProfitability, canonicalCampaigns, metricBasis);
    const hasProfit = campaigns.some((campaign) => campaign.profit != null) || (totalProfitMetric.value !== null && totalProfitMetric.value !== undefined);
    const hasPoas = campaigns.some((campaign) => campaign.poas != null);
    const usesProfitClassification = campaigns.some((campaign) => campaign.profitClassificationAvailable);
    const descriptionKey = campaignDescriptionKey(snapshot, usesProfitClassification);
    const periodVerdictsAvailable = !summaryUnavailable;
    const meta = segmentMeta(t, usesProfitClassification);
    const segmentCampaigns = !periodVerdictsAvailable || selectedSegment === "all" ? campaigns : campaigns.filter((campaign) => campaign.segment === selectedSegment);
    const filteredCampaigns = filterRowsBySearch(segmentCampaigns, tableSearch, (campaign) => [campaign.name, campaign.channelName]);
    const {sortedRows: sortedCampaigns, sort: tableSort, toggleSort} = useSortableRows(
        filteredCampaigns,
        "spend",
        campaignTableValue,
    );
    const tablePages = Math.max(1, Math.ceil(sortedCampaigns.length / tablePageSize));
    const normalizedTablePage = Math.min(tablePage, tablePages);
    const visibleCampaigns = sortedCampaigns.slice((normalizedTablePage - 1) * tablePageSize, normalizedTablePage * tablePageSize);

    useEffect(() => {
        setTablePage(1);
    }, [selectedSegment, campaigns.length, tableSearch]);
    const segmentCounts = useMemo(() => {
        return campaigns.reduce((counts, campaign) => {
            counts[campaign.segment] = (counts[campaign.segment] ?? 0) + 1;
            return counts;
        }, {});
    }, [campaigns]);
    const totals = useMemo(() => campaigns.reduce((accumulator, campaign) => {
        if (campaign.financeScope === "raw_provider") return accumulator;
        accumulator.spend += moneyAmount(campaign.spend);
        accumulator.turnover += moneyAmount(campaign.turnover);
        accumulator.orders += campaign.orders;
        return accumulator;
    }, {spend: 0, turnover: 0, orders: 0}), [campaigns]);
    const currency = campaigns.find((campaign) => campaign.spend?.currency)?.spend?.currency ?? projectCurrency(project);
    const periodSpend = snapshotPeriodValue(totals.spend, snapshot);
    const periodOrders = snapshotPeriodValue(totals.orders, snapshot);
    const campaignCount = snapshotHasPublishedDays(snapshot) ? campaigns.length : null;
    const aboveBreakEvenCampaigns = periodVerdictsAvailable ? rankCompleteAboveBreakEvenCampaigns(campaigns, 6, metricBasis) : [];
    const lossCampaigns = campaigns.filter((campaign) => campaign.profitClassificationAvailable && campaign.segment === CampaignSegment.Loss).sort((a, b) => moneyAmount(a.profit) - moneyAmount(b.profit)).slice(0, 6);
    const topSpendCampaigns = [...canonicalCampaigns].sort((a, b) => moneyAmount(b.spend) - moneyAmount(a.spend)).slice(0, 10);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const spendProfitOption = {
        color: ["#7c3aed", "#16a34a"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 28, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...topSpendCampaigns].reverse().map((campaign) => campaign.name),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: [...topSpendCampaigns].reverse().map((campaign) => moneyToMajor(campaign.spend)),
                barMaxWidth: 24,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: [...topSpendCampaigns].reverse().map((campaign) => partialProfitabilityChartPoint(nullableMoneyMajorUnitAmount(campaign.profit), campaign.partialProfitability, campaign.profitIsPartial, t)),
                barMaxWidth: 24,
            },
        ],
    };

    const profitRankingOption = {
        color: ["#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...lossCampaigns].reverse().map((campaign) => campaign.name),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: [...lossCampaigns].reverse().map((campaign) => nullableMoneyMajorUnitAmount(campaign.profit)),
                barMaxWidth: 24,
            },
        ],
    };
    const campaignTableActions = <div className="flex flex-wrap items-center justify-end gap-2">
        <button
            type="button"
            onClick={() => setSelectedSegment("all")}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedSegment === "all" ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {t("reporting_lab.campaigns.all", {defaultValue: "Vše"})} ({renderNumber(campaigns.length)})
        </button>
        {Object.values(CampaignSegment).map((segment) => <button
            key={segment}
            type="button"
            onClick={() => setSelectedSegment(segment)}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedSegment === segment ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {meta[segment].label} ({renderNumber(segmentCounts[segment] ?? 0)})
        </button>)}
    </div>;
    const campaignTableTitle = <ExpandablePanelTitle
        title={t("reporting_lab.campaigns.table", {defaultValue: "Přehled kampaní"})}
        expanded={tableExpanded}
        onToggle={() => setTableExpanded((expanded) => !expanded)}
        expandLabel={t("reporting_lab.table.expand", {defaultValue: "Rozšířit"})}
        collapseLabel={t("reporting_lab.table.collapse", {defaultValue: "Zúžit"})}
    />;
    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.campaigns", {defaultValue: "Kampaně"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[loadError ? campaignsLoadErrorAlert(loadErrorMessage, t) : snapshotCompletenessAlert(snapshot, t)]}
     intro={descriptionKey && <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.campaigns.title", {defaultValue: "K čemu slouží přehled kampaní"})}
                >
                    {usesProfitClassification
                        ? t("reporting_lab.descriptions.campaigns.text", {defaultValue: "Rozděluje kampaně podle obratu, POAS, hrubého zisku po reklamě a objemu dat. Objednávková báze zahrnuje všechny nestornované objednávky přiřazené kampaním, včetně objednávek bez známého stavu. Doporučení ke škálování vyžaduje projektové cíle a není odvozováno z univerzálního limitu."})
                        : t("reporting_lab.descriptions.campaigns.marketing_text", {defaultValue: "Vyhodnocuje nestornované objednávky přiřazené kampaním, včetně objednávek bez známého stavu, podle obratu, nákladů a objemu dat. Nákupní ceny nejsou dostupné pro úplný výpočet zisku."})}
                </ReportingPageDescription>}>
        {!loadError && <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={campaignMediaQuality}/>
                <ReportingCostCoverageContext data={marketingPartialProfitability} reportContext="campaigns" loading={loading}/>
                {hasProfit && (totalProfitMetric.value === null || totalProfitMetric.value === undefined) && periodVerdictsAvailable && <div className="app-surface col-span-12 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    {t("reporting_lab.campaigns.incomplete_profit", {defaultValue: "Celkový hrubý zisk po reklamě není dostupný, protože některé kampaně nemají úplné nákupní náklady."})}
                </div>}
                {periodVerdictsAvailable && <div className="app-surface col-span-12 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{t("reporting_lab.campaigns.segment_rules_title", {defaultValue: "Jak číst segmenty kampaní"})}</div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                        <span><strong>{meta[CampaignSegment.AboveBreakEven].label}</strong>: {usesProfitClassification
                            ? t("reporting_lab.campaigns.segment_rules.above_break_even", {defaultValue: "POAS alespoň 1,0x a nezáporná marže"})
                            : t("reporting_lab.campaigns.segment_rules.performing", {defaultValue: "obrat je alespoň stejně vysoký jako marketingový spend"})}</span>
                        <span><strong>{meta[CampaignSegment.Loss].label}</strong>: {usesProfitClassification
                            ? t("reporting_lab.campaigns.segment_rules.loss", {defaultValue: "POAS pod 1,0x nebo záporná marže"})
                            : t("reporting_lab.campaigns.segment_rules.underperforming", {defaultValue: "marketingový spend je vyšší než obrat"})}</span>
                        <span><strong>{meta[CampaignSegment.LowData].label}</strong>: {t("reporting_lab.campaigns.segment_rules.low_data", {defaultValue: "méně než 3 objednávky"})}</span>
                    </div>
                </div>}

                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.campaigns.total_campaigns", {defaultValue: "Kampaně"})} value={campaignCount === null ? "—" : renderNumber(campaignCount)} meta={campaignCount === null ? t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"}) : t("reporting_lab.campaigns.with_data", {defaultValue: "Se spendem, objednávkami nebo kliky"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={meta[CampaignSegment.AboveBreakEven].label} value={!periodVerdictsAvailable || campaignCount === null ? "—" : renderNumber(segmentCounts[CampaignSegment.AboveBreakEven] ?? 0)} meta={!periodVerdictsAvailable ? snapshotUnavailableShort(snapshot, t) : campaignCount === null ? t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"}) : t("reporting_lab.campaigns.above_break_even_meta", {defaultValue: "Nejde automaticky o doporučení ke škálování"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.marketing_spend", {defaultValue: "Marketingové náklady"})} value={renderCurrencyValue(periodSpend === null ? null : {amount: periodSpend, currency}, true)} meta={<>{t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Objednávky bez storen"})}: <strong>{periodOrders === null ? "—" : renderNumber(periodOrders)}</strong></>}/>
                </div>
                {hasProfit && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={totalProfitMetric.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.kpi.profit", {defaultValue: "Hrubý zisk po reklamě"})} value={totalProfitMetric.value === null || totalProfitMetric.value === undefined ? "-" : renderCurrencyValue(typeof totalProfitMetric.value === "object" ? totalProfitMetric.value : {amount: totalProfitMetric.value, currency}, true)} meta={totalProfitMetric.value === null || totalProfitMetric.value === undefined ? (summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : t("reporting_lab.cost_quality.profit_unavailable_short", {defaultValue: "Není dostupný"})) : `${meta[CampaignSegment.Loss].label}: ${renderNumber(segmentCounts[CampaignSegment.Loss] ?? 0)}`} info={<><ProfitabilityMetricTooltip metric="profit" partialProfitability={marketingPartialProfitability} rowScopes={campaigns.map((campaign) => campaign.partialProfitabilityMarketing)} t={t}/>{(totalProfitMetric.value === null || totalProfitMetric.value === undefined) && <div className="mt-1">{summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné."}) : t("reporting_lab.campaigns.incomplete_profit", {defaultValue: "Celkový hrubý zisk po reklamě není dostupný, protože některé kampaně nemají úplné nákupní náklady."})}</div>}</>} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})} metaTone={totalProfitMetric.value === null || totalProfitMetric.value === undefined ? "unavailable" : "default"} tone={!totalProfitMetric.partial && totalProfitMetric.value !== null && totalProfitMetric.value !== undefined && Number(typeof totalProfitMetric.value === "object" ? totalProfitMetric.value.amount : totalProfitMetric.value) < 0 ? "bad" : "default"}/>
                </div>}

                {hasProfit && <Panel loading={loading} title={t("reporting_lab.campaigns.spend_profit", {defaultValue: "Hrubý zisk po reklamě a marketing top kampaní"})} className="col-span-12 xl:col-span-7">
                    <EChart option={spendProfitOption} className="h-96"/>
                </Panel>}
                {lossCampaigns.length > 0 && periodVerdictsAvailable && <Panel loading={loading} title={t("reporting_lab.campaigns.loss_ranking", {defaultValue: "Kampaně k omezení"})} className="col-span-12 xl:col-span-5">
                    <EChart option={profitRankingOption} className="h-96"/>
                </Panel>}

                <div className="col-span-12 grid grid-cols-12 gap-4">
                {usesProfitClassification && hasPoas && periodVerdictsAvailable && <Panel loading={loading} title={t("reporting_lab.campaigns.above_break_even", {defaultValue: "Nejziskovější kampaně"})} className={`col-span-12 ${tableExpanded ? "order-2 xl:col-span-12" : "order-1 xl:col-span-4"}`}>
                    <div className="space-y-3">
                        {aboveBreakEvenCampaigns.map((campaign) => <div key={campaign.id} className="app-surface rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                            <div className="font-semibold text-slate-900 dark:text-slate-100">{campaign.name}</div>
                            <div className="mt-1 text-xs text-slate-500">{campaign.channelName}</div>
                            <div className="mt-2 flex items-center justify-between text-sm">
                                <span className={`whitespace-nowrap font-semibold ${moneyAmount(campaign.profit) < 0 ? "text-datixo-negative" : "text-slate-700 dark:text-slate-200"}`}>{renderCurrencyValue(campaign.profit, true)}</span>
                                <span className="font-semibold text-emerald-600">POAS {renderRatioValue(campaign.poas)}</span>
                            </div>
                        </div>)}
                        {aboveBreakEvenCampaigns.length === 0 && <div className="text-sm text-slate-500">{t("reporting_lab.campaigns.no_above_break_even", {defaultValue: "Pro vybrané období nejsou ziskové kampaně s dostatečným objemem dat."})}</div>}
                    </div>
                </Panel>}

                <Panel loading={loading} title={campaignTableTitle} actions={periodVerdictsAvailable ? campaignTableActions : null} className={`col-span-12 ${tableExpanded || !hasProfit || !hasPoas ? "order-1 xl:col-span-12" : "order-2 xl:col-span-8"}`}>
                    <TableSearch value={tableSearch} onChange={setTableSearch} visible={segmentCampaigns.length > 15} t={t}/>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <SortableHeader label={t("reporting_lab.campaigns.campaign", {defaultValue: "Kampaň"})} sortKey="campaign" sort={tableSort} onSort={toggleSort}/>
                                <SortableHeader label={t("reporting_lab.table.channel", {defaultValue: "Kanál"})} sortKey="channel" sort={tableSort} onSort={toggleSort}/>
                                {periodVerdictsAvailable && <SortableHeader label={t("reporting_lab.campaigns.segment_title", {defaultValue: "Segment"})} sortKey="segment" sort={tableSort} onSort={toggleSort}/>}
                                <SortableHeader label={t("reporting_lab.table.marketing", {defaultValue: "Marketing"})} sortKey="spend" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right"/>
                                {hasProfit && <SortableHeader
                                    label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})}
                                    tooltip={<ProfitabilityMetricTooltip metric="profit" partialProfitability={marketingPartialProfitability} rowScopes={campaigns.map((campaign) => campaign.partialProfitabilityMarketing)} t={t}/>}
                                    sortKey="profit"
                                    sort={tableSort}
                                    onSort={toggleSort}
                                    align="right"
                                />}
                                {[["ROAS", "roas"], ["PNO", "pno"]].map(([label, key]) => <SortableHeader key={key} label={label} sortKey={key} sort={tableSort} onSort={toggleSort} align="right"/>)}
                                {hasPoas && <SortableHeader label="POAS" sortKey="poas" sort={tableSort} onSort={toggleSort} align="right"/>}
                                <SortableHeader label={t("reporting_lab.table.clicks", {defaultValue: "Kliky"})} sortKey="clicks" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.impressions", {defaultValue: "Imprese"})} sortKey="impressions" sort={tableSort} onSort={toggleSort} align="right"/>
                                {[["CTR", "ctr"], ["CPC", "cpc"], ["CPM", "cpm"]].map(([label, key]) => <SortableHeader key={key} label={label} tooltip={campaignMetricTooltip(key, t)} sortKey={key} sort={tableSort} onSort={toggleSort} align="right"/>)}
                                <SortableHeader label={t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."})} sortKey="costPerOrder" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.conversion_rate", {defaultValue: "Konverze"})} tooltip={campaignMetricTooltip("conversionRate", t)} sortKey="conversionRate" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.aov", {defaultValue: "AOV"})} sortKey="averageOrderValue" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.orders_short", {defaultValue: "Obj."})} sortKey="orders" sort={tableSort} onSort={toggleSort} align="right"/>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {visibleCampaigns.map((campaign) => <tr key={campaign.id}>
                                <td className="max-w-[340px] truncate py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{campaign.name}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{campaign.channelName}</td>
                                {periodVerdictsAvailable && <td className="py-3 pr-4"><SegmentBadge segment={campaign.segment} meta={meta}/></td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300"><div>{renderCurrencyValue(campaign.spend, true)}</div>{mediaFinanceScopeLabel(campaign.financeScope, t, i18n?.language) && <div className="text-xs text-slate-500 dark:text-slate-400">{mediaFinanceScopeLabel(campaign.financeScope, t, i18n?.language)}</div>}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.turnover, true)}</td>
                                {hasProfit && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.profit, true)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderRatioValue(campaign.roas)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.pno === null ? "-" : `${formatChartNumber(campaign.pno)} %`}</td>
                                {hasPoas && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderRatioValue(campaign.poas)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.clicks)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.impressions)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.ctr === null ? "-" : `${formatChartNumber(campaign.ctr)} %`}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.cpc, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.cpm, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.costPerOrder, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.conversionRate === null ? "-" : `${formatChartNumber(campaign.conversionRate)} %`}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.averageOrderValue, true)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.orders)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination page={normalizedTablePage} pageSize={tablePageSize} totalItems={filteredCampaigns.length} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>
                </Panel>
                </div>
            </div>}
    </WideSecureLayout>;
}
